import { Suspense, lazy } from 'react';
import './DriverQuote.css';

// Lazy-load Three.js only when a 3D model is needed
const Car3DViewer = lazy(() => import('./DriverProfileCar3D'));

/* ---------- Types ---------- */

interface ResolvedDriver {
  id: string;
  first_name: string;
  last_name: string;
  nationality: string | null;
  profile_image_url: string | null;
  half_body_image_url: string | null;
  full_body_image_url: string | null;
  stats: unknown;
}

interface ResolvedCar {
  id: string;
  car_number: string;
  car_name: string | null;
  manufacturer: string | null;
  model_3d_url: string | null;
  side_image_url: string | null;
  number_image_url: string | null;
}

interface ResolvedSponsor {
  id: string;
  name: string;
  logo_light_url: string;
  logo_dark_url: string | null;
}

/* ---------- Component ---------- */

interface DriverQuoteProps {
  orgName: string;
  primaryColor: string;
  config: Record<string, unknown> | null;
}

export default function DriverQuote({ config }: DriverQuoteProps) {
  // Data is resolved server-side in the get_showcase_by_token RPC
  const driver = (config?._resolved_driver as ResolvedDriver) ?? null;
  const raceCar = (config?._resolved_car as ResolvedCar) ?? null;
  const sponsors = (config?._resolved_sponsors as ResolvedSponsor[]) ?? [];
  const carModelUrl = raceCar?.model_3d_url ?? (config?.carModelUrl as string) ?? null;
  const quote = (config?.quote as string) ?? '';
  const backgroundImageUrl = (config?.backgroundImageUrl as string) ?? null;

  if (!driver) {
    return (
      <div className="driver-quote driver-quote--empty">
        <p className="driver-quote__empty-text">No driver configured</p>
      </div>
    );
  }

  // Prefer full-body for TV display, fall back to half-body, then profile
  const driverPhoto = driver.half_body_image_url ?? driver.full_body_image_url ?? driver.profile_image_url;
  const hasPhoto = !!driverPhoto;
  const has3D = !!carModelUrl;
  const hasCarImage = !!raceCar?.side_image_url;

  // Build sponsor track — duplicate enough times for seamless scroll loop.
  const minItems = 24;
  const repeatCount = sponsors.length > 0 ? Math.max(2, Math.ceil(minItems / sponsors.length) * 2) : 0;
  const sponsorTrackItems = Array.from({ length: repeatCount }, () => sponsors).flat();

  return (
    <div className="driver-quote">

      {/* ── Background image — full bleed, overrides car ── */}
      {backgroundImageUrl && (
        <div className="driver-quote__bg-image">
          <img src={backgroundImageUrl} alt="" draggable={false} />
        </div>
      )}

      {/* ── 3D Car — full-screen rotating GLB model as background ── */}
      {!backgroundImageUrl && has3D && (
        <div className="driver-quote__car-3d">
          <Suspense fallback={null}>
            <Car3DViewer modelUrl={carModelUrl!} />
          </Suspense>
        </div>
      )}
      {!backgroundImageUrl && !has3D && hasCarImage && (
        <div className="driver-quote__car-image">
          <img src={raceCar!.side_image_url!} alt="Race car" draggable={false} />
        </div>
      )}

      {/* ── Vignette — multi-directional edge darkening ── */}
      <div className="driver-quote__vignette" />

      {/* ── Driver photo — left side ── */}
      {hasPhoto && (
        <img
          className="driver-quote__photo"
          src={driverPhoto!}
          alt={`${driver.first_name} ${driver.last_name}`}
          draggable={false}
        />
      )}

      {/* ══ QUOTE CARD — glassmorphic, slides in from the right ══ */}
      {quote && (
        <div className="driver-quote__quote-card">
          <div className="driver-quote__quote-mark">"</div>
          <div className="driver-quote__quote-text">{quote}</div>
          <div className="driver-quote__quote-attribution">
            — {driver.first_name} {driver.last_name}
          </div>
        </div>
      )}

      {/* ══ BOTTOM SECTION — sponsors only ══ */}
      <div className="driver-quote__bottom">

        {/* Sponsors card — scrolling logo strip with gradient border */}
        {sponsors.length > 0 && (
          <div className="driver-quote__sponsor-bar">
            <div className="driver-quote__sponsor-track">
              {sponsorTrackItems.map((s, i) => (
                <img
                  key={`${s.id}-${i}`}
                  className="driver-quote__sponsor-logo"
                  src={s.logo_dark_url ?? s.logo_light_url}
                  alt={s.name}
                  draggable={false}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
